<template>
  <div v-hotkey="keymap">
    <StubModeBanner />
    <DismissibleBanner
      v-if="isSpoofing && account"
      type="error"
      :square="true"
      :showIcon="false"
      :expiryReference="spoofBannerExpiryReference"
      :ttl="spoofBannerTTL"
      :useSessionStorage="true"
      messageOnClose="Spoofing banner on this account will be closed for 15 minutes."
      style="
        text-align: center;
        position: sticky;
        z-index: 999;
        width: 100%;
        top: 0;
      "
    >
      <template slot="description">
        <b>Warning!</b> You are spoofing a real user on account
        <b>{{ account.name }}</b
        >. Please be careful!
        <a-button style="margin-left: 10px" @click="onSpoofCancelClicked">
          Stop Spoofing
        </a-button>
      </template>
    </DismissibleBanner>
    <div class="sidebar-splash-text">
      {{ sidebarSplashText }}
    </div>
    <a-layout id="main-layout" class="top-layout">
      <Sidebar
        :menuItems="menuItems"
        :collapsed.sync="collapsed"
        :taskCount="taskCount"
        :showTaskButton="showTaskButton"
        :showSideBarCta="showSideBarCta"
        :demoLink="demoLink"
        :environment="environment"
        :showSoftwareFactoryRoadmap="softwareFactoryEnabled"
        @onToggleTaskDrawer="handleToggleTaskDrawer"
        @sideBarCtaClicked="handleSideBarCtaClicked"
      />
      <a-layout
        id="main-content"
        :class="{ 'main-layout-content': true, 'sider-collapsed': collapsed }"
      >
        <PlanExpiryBanner
          :daysLeft="planExpiryDetails.daysLeft"
          :planName="planExpiryDetails.paymentPlan"
          :canExpire="planExpiryDetails.canExpire"
          :creditsRemaining="planExpiryDetails.creditsRemaining"
        />
        <a-layout-header style="" class="global-header">
          <a-icon
            class="trigger"
            :type="collapsed ? 'menu-unfold' : 'menu-fold'"
            @click="() => (collapsed = !collapsed)"
          />
          <div class="right">
            <LongRunningTaskDropdown v-if="showLongRunningTasks" />
            <a-tooltip :title="`Quick Help Menu`">
              <a-icon
                type="question-circle"
                :style="docsIconStyle"
                @click="showHelp()"
              />
            </a-tooltip>
            <a-icon
              v-if="beamerEnabled"
              type="notification"
              style="padding: 12px; margin-right: 12px; font-size: 1.2em"
              class="beamerTrigger"
            />
            <SearchBar />
            <a-dropdown class="action" :trigger="['click']">
              <div style="display: flex; align-items: center; float: right">
                <UserWithAvatar
                  v-if="actor"
                  :userName="actor.user.name"
                  :gravatarHref="actor.user.avatar"
                />
                <a-icon
                  v-if="hasProfileLinks"
                  type="down"
                  style="padding-left: 3px; font-size: 14px"
                />
              </div>
              <a-menu v-if="hasProfileLinks" slot="overlay" :selectable="false">
                <template v-if="profileLinks.length > 1">
                  <a-menu-item
                    v-for="(item, index) in filteredProfileLinks"
                    :key="index + 1"
                  >
                    <MenuItemLink :item="item" />
                  </a-menu-item>
                  <a-menu-divider />
                </template>
                <a-menu-item :key="profileLinks.length">
                  <MenuItemLink :item="profileLinks[profileLinks.length - 1]" />
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </div>
        </a-layout-header>

        <slot name="flash" />

        <a-layout-content v-if="actor && account" class="global-content">
          <slot name="content">
            <div></div>
          </slot>
        </a-layout-content>

        <JoinTeamsModal v-if="displayTeamsCta && actor" />

        <TaskDrawer v-if="account" ref="taskDrawer" :count="taskCount" />
        <div class="expando" />
        <CopyrightFooter />
      </a-layout>
    </a-layout>
    <a-modal v-model="planExpiredModalVisible" :footer="null">
      <CallToAction
        buttonText="Contact Us"
        type="primary"
        :link="mailToSupport"
      >
        <img
          src="/status_images/pilot_timer.svg"
          style="height: 200px; width: 200px; padding-bottom: 10px"
          class="call-to-action-image"
        />
        <h3>Your {{ paymentPlan }}</h3>
        <p style="margin-bottom: 0px">
          To continue using {{ $t("brandName") }}, please contact your account
          manager or <a :href="mailToSupport">{{ supportAddress }}</a
          >.
        </p>
      </CallToAction>
    </a-modal>
    <HelpModal ref="helpModal" />
  </div>
</template>

<script>
import { computed } from "vue";
import MenuItemLink from "@/components/MenuItemLink.vue";
import CopyrightFooter from "./CopyrightFooter.vue";
import JoinTeamsModal from "@/components/JoinTeamsModal.vue";
import DismissibleBanner from "@/components/molecules/DismissibleBanner.vue";
import CallToAction from "@/components/CallToAction.vue";
import UserWithAvatar from "@/components/atoms/UserWithAvatar.vue";
import { internalNotifications } from "@/mixins/internalNotifications.js";
import { errors } from "@/mixins/errors.js";
import HelpModal from "@/components/molecules/HelpModal.vue";
import PlanExpiryBanner from "@/components/molecules/PlanExpiryBanner.vue";
import StubModeBanner from "@/components/molecules/StubModeBanner.vue";
import LongRunningTaskDropdown from "@/components/organisms/LongRunningTaskDropdown.vue";
import SearchBar from "@/components/SearchBar.vue";
import Sidebar from "@/components/organisms/Sidebar.vue";
import { isArray } from "lodash";

import store from "@/store/index.js";
import application from "@/modules/application/index.js";
import members from "@/modules/teams/members/index.js";
import teams from "@/modules/teams/index.js";
import integrations from "@/modules/integrations/index.js";
import tracking from "@/modules/tracking/index.js";
import { mapActions, mapState } from "vuex";
import { parseUrlParamsToObject } from "@/shared/url_parser.js";
import { PaymentPlans } from "@/shared/payment_plans_helper.js";
import { OPSLEVEL_DEMO_LINK } from "@/shared/links.js";

const NavCollapsedKey = "main-nav-collapsed";
const SpoofBannerTTL = 900; // 15 minutes in seconds

import * as Routes from "@/routes.js";
import mermaid from "mermaid";
import TaskDrawer from "@/tasks/TaskDrawer.vue";

function initCollapsed() {
  const collapsed = localStorage.getItem(NavCollapsedKey);

  return collapsed ? collapsed === "true" : false;
}

export default {
  components: {
    SearchBar,
    MenuItemLink,
    CopyrightFooter,
    JoinTeamsModal,
    DismissibleBanner,
    CallToAction,
    UserWithAvatar,
    HelpModal,
    PlanExpiryBanner,
    StubModeBanner,
    LongRunningTaskDropdown,
    TaskDrawer,
    Sidebar,
  },

  mixins: [internalNotifications, errors],

  inject: {
    brand: { default: "opslevel" },
    isTidra: { default: false },
  },

  provide() {
    return {
      systemToggles: this.systemToggles,
      routes: this.routes,
      account: computed(() => this.account),
      currentUser: computed(() => this.actor),
      featureFlags: computed(() =>
        this.featureFlags.filter((f) => f.enabled).map((f) => f.key),
      ),
    };
  },

  props: {
    menuItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    profileLinks: {
      type: Array,
      required: false,
      default: () => [],
    },
    displayTeamsCta: {
      type: Boolean,
      default: true,
    },
    urls: {
      type: Object,
      default: () => {},
    },
    systemToggles: {
      type: Array,
      default: () => [],
    },
    beamerEnabled: {
      type: Boolean,
      default: false,
    },
    planExpiryDetails: {
      type: Object,
      required: false,
      default: () => {},
    },
    isSpoofing: {
      type: Boolean,
      required: false,
      default: false,
    },
    taskCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  store,

  data() {
    return {
      collapsed: initCollapsed(),
      spoofBannerTTL: SpoofBannerTTL,
      // we set this on data so that the modal can be dismissed
      planExpiredModalVisible:
        Boolean(this.planExpiryDetails.expired) && !this.isTidra,
    };
  },

  computed: {
    ...mapState({
      isCreatingSampleGitIntegration: (state) =>
        state.sampleIntegrations.isCreatingSampleGitIntegration,
      environment: (state) => state.application.environment,
      featureFlags: (state) => state.application.featureFlags,
      actor: (state) => state.application?.actor,
      account: (state) => state.application?.account,
    }),
    sidebarSplashText: function () {
      const isProd =
        this.environment === "production" ||
        parseUrlParamsToObject(window.location.search).simulateProd !==
          undefined;

      return isProd ? "" : this.environment;
    },
    hasProfileLinks: function () {
      return this.profileLinks && this.profileLinks.length > 0;
    },
    showTaskButton() {
      if (this.brand === "tidra") {
        return false;
      }

      return (
        this.featureFlags &&
        this.featureFlags.some(
          (flag) => flag.key === "task_drawer" && flag.enabled,
        )
      );
    },
    softwareFactoryEnabled() {
      return Boolean(
        this.featureFlags?.some(
          (flag) => flag.key === "software_factory" && flag.enabled,
        ),
      );
    },
    filteredProfileLinks() {
      return this.profileLinks.slice(0, -1);
    },
    supportAddress() {
      return this.account?.supportEmail ?? "";
    },
    mailToSupport() {
      return `mailto:${this.supportAddress}?subject=Account%20Expired`;
    },
    showSideBarCta() {
      return (
        this.brand === "opslevel" &&
        this.planExpiryDetails.paymentPlan === "Free Trial"
      );
    },
    spoofBannerExpiryReference() {
      return this.account.id + "_spoofBannerExpiryReference";
    },
    docsIconStyle() {
      return this.beamerEnabled
        ? "padding: 12px; font-size: 1.2em;"
        : "padding: 12px; margin-right: 6px; font-size: 1.2em;";
    },
    keymap() {
      return {
        "shift+/": this.showHelp,
        "ctrl+shift+1": () =>
          this.isTidra
            ? this.openPage("initiatives")
            : this.openPage("dashboard"),
        "ctrl+shift+2": () => this.openPage("components"),
      };
    },
    showLongRunningTasks() {
      return (
        this.planExpiryDetails.paymentPlan === PaymentPlans.freeTrial &&
        !this.hasSystemToggle("disable_websocket_connections")
      );
    },
    routes() {
      return Routes;
    },
    paymentPlan() {
      if (this.planExpiryDetails.paymentPlan === "Expired") {
        return "Account has expired.";
      } else {
        return `${this.planExpiryDetails.paymentPlan} has ended.`;
      }
    },
    demoLink() {
      return this.isTidra ? null : OPSLEVEL_DEMO_LINK;
    },
  },

  watch: {
    collapsed(newValue) {
      localStorage.setItem(NavCollapsedKey, newValue);
      this.setIsSidebarCollapsed({ collapsed: newValue });
    },
  },

  mounted() {
    mermaid.initialize();

    document.addEventListener("canduButtonClicked", (event) => {
      if (
        event?.detail?.action?.eventName === "create-sample-git-integration"
      ) {
        if (this.isCreatingSampleGitIntegration) {
          return;
        }

        this.createSampleGitIntegration();
      }
    });
  },

  created() {
    this.$store.registerModuleOnce("application", application);
    this.$store.registerModuleOnce("members", members);
    this.$store.registerModuleOnce("joinTeams", teams);
    this.$store.registerModuleOnce("sampleIntegrations", integrations);
    this.$store.registerModuleOnce("tracking", tracking);
    this.fetchApplicationConfigs();
    this.setIsSidebarCollapsed({ collapsed: this.collapsed });

    this.$store.watch(
      (state) => state.sampleIntegrations.errorsCreatingSampleGitIntegration,
      (errorsCreatingSampleGitIntegration) => {
        if (errorsCreatingSampleGitIntegration) {
          this.showErrorMessages("", errorsCreatingSampleGitIntegration);
        }
      },
    );
    this.$store.watch(
      (state) => state.sampleIntegrations.sampleGitIntegration,
      (sampleGitIntegration) => {
        location.assign(sampleGitIntegration.href);
      },
    );
  },

  methods: {
    ...mapActions({
      fetchApplicationConfigs: "application/fetchApplicationConfigs",
      setIsSidebarCollapsed: "application/setIsSidebarCollapsed",
      createSampleGitIntegration:
        "sampleIntegrations/createSampleGitIntegration",
      trackUserClick: "tracking/trackUserClick",
    }),
    handleToggleTaskDrawer() {
      if (this.$refs.taskDrawer.visible) {
        this.$refs.taskDrawer.close();
      } else {
        this.$refs.taskDrawer.open();
      }
    },
    isModalVisibleOnScreen() {
      // converting HTMLCollection to an Array
      const foundModalsInDOM = [
        ...document.getElementsByClassName("ant-modal"),
      ];

      // offset parent can be used to determine if a DOM element is visible on the screen
      // source: https://stackoverflow.com/questions/19669786/check-if-element-is-visible-in-dom
      const isVisible = (domElement) => Boolean(domElement.offsetParent);

      return foundModalsInDOM.some(isVisible);
    },
    showHelp() {
      if (this.isModalVisibleOnScreen()) {
        return;
      }

      this.$refs.helpModal.show();
    },
    openPage(page) {
      window.location.assign(`/${page}`);
    },
    onSpoofCancelClicked() {
      if (this.urls["stop_spoof"]) {
        window.location.assign(this.urls["stop_spoof"]);
      }
    },
    hasSystemToggle(toggle) {
      if (!isArray(this.systemToggles)) {
        return false;
      } else {
        return this.systemToggles.includes(toggle);
      }
    },
    handleSideBarCtaClicked() {
      this.trackUserClick("freeTrialSideBarCtaClicked");
    },
  },
};
</script>

<style scoped lang="scss">
.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    background-color: #e6f7ff;
  }
}

.sidebar-splash-text {
  position: absolute;
  font-size: 8vw;
  color: #fff;
  opacity: 0.1;
  line-height: 8vw;
  transform: rotate(80deg);
  transform-origin: center left;
  z-index: 2000;
  pointer-events: none;
  left: 4vw;
  top: 10vh;
}
.global-header {
  flex: 0 1 auto;
  background: #fff;
  padding: 0 12px 0 0;

  .right {
    float: right;

    .action {
      margin-right: -12px;
      padding: 0 24px 0 12px;
      display: inline-block;
      &:hover {
        background-color: #e6f7ff; /* TODO: Access this as a less variable from ant-design-vue */
        cursor: pointer;
      }
    }
  }
}

.main-layout-content {
  margin-left: 200px;

  &.sider-collapsed {
    margin-left: 80px;
  }
}
</style>
